import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import KPICard from './components/KPICard';
import TransactionChart from './components/TransactionChart';
import LiveTransactionFeed from './components/LiveTransactionFeed';
import PaymentMethodPerformance from './components/PaymentMethodPerformance';
import FilterControls from './components/FilterControls';
import { transactionsService, merchantsService } from '../../services/supabaseService';

const TransactionOverview = () => {
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState({
    totalVolume: { value: 0, trend: 'up', trendValue: '+0%', sparkline: [], icon: 'Activity', status: 'success' },
    successRate: { value: 0, trend: 'up', trendValue: '+0%', sparkline: [], icon: 'CheckCircle2', status: 'success' },
    revenue: { value: 0, trend: 'up', trendValue: '+0%', sparkline: [], icon: 'DollarSign', status: 'success' },
    activeMerchants: { value: 0, trend: 'up', trendValue: '+0%', sparkline: [], icon: 'Users', status: 'neutral' }
  });
  const [chartData, setChartData] = useState([]);
  const [liveTransactions, setLiveTransactions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch transactions
        const { data: transactions } = await transactionsService?.getAll({ limit: 50 });
        
        // Fetch merchant statistics
        const { data: merchantStats } = await merchantsService?.getStatistics();
        
        // Fetch transaction statistics
        const { data: transactionStats } = await transactionsService?.getStatistics();

        if (transactions) {
          setLiveTransactions(transactions?.slice(0, 20) || []);
          
          // Calculate KPI data
          const totalVolume = transactions?.length || 0;
          const successCount = transactions?.filter(t => t?.status === 'success')?.length || 0;
          const successRate = totalVolume > 0 ? (successCount / totalVolume) * 100 : 0;
          const totalRevenue = transactions?.reduce((sum, t) => sum + (parseFloat(t?.amount) || 0), 0);
          
          setKpiData({
            totalVolume: {
              value: totalVolume,
              trend: 'up',
              trendValue: '+12.5%',
              sparkline: [3200, 3800, 3500, 4200, 4800, 4500, 5100, totalVolume],
              icon: 'Activity',
              status: 'success'
            },
            successRate: {
              value: successRate,
              trend: successRate > 95 ? 'up' : 'down',
              trendValue: '+2.3%',
              sparkline: [94, 95, 94.5, 96, 96.5, 96.2, 96.8, successRate],
              icon: 'CheckCircle2',
              status: 'success'
            },
            revenue: {
              value: totalRevenue,
              trend: 'up',
              trendValue: '+18.2%',
              sparkline: [6500, 7200, 7000, 7800, 8200, 8000, 8500, totalRevenue / 1000000],
              icon: 'DollarSign',
              status: 'success'
            },
            activeMerchants: {
              value: merchantStats?.activeMerchants || 0,
              trend: 'up',
              trendValue: '+5.8%',
              sparkline: [1100, 1150, 1180, 1200, 1220, 1230, 1240, merchantStats?.activeMerchants || 0],
              icon: 'Users',
              status: 'neutral'
            }
          });

          // Generate chart data from transactions
          const hourlyData = {};
          transactions?.forEach(t => {
            const hour = new Date(t?.timestamp)?.getHours();
            const hourKey = `${hour}:00`;
            if (!hourlyData?.[hourKey]) {
              hourlyData[hourKey] = { time: hourKey, volume: 0, successCount: 0, total: 0 };
            }
            hourlyData[hourKey].volume += 1;
            hourlyData[hourKey].total += 1;
            if (t?.status === 'success') {
              hourlyData[hourKey].successCount += 1;
            }
          });

          const chartDataArray = Object.values(hourlyData)?.map(d => ({
            time: d?.time,
            volume: d?.volume,
            successRate: d?.total > 0 ? (d?.successCount / d?.total) * 100 : 0
          }));
          setChartData(chartDataArray);

          // Calculate payment method performance
          const methodStats = {};
          transactions?.forEach(t => {
            const method = t?.paymentMethod || 'Unknown';
            if (!methodStats?.[method]) {
              methodStats[method] = { name: method, volume: 0, revenue: 0, successCount: 0, total: 0 };
            }
            methodStats[method].volume += 1;
            methodStats[method].total += 1;
            methodStats[method].revenue += parseFloat(t?.amount) || 0;
            if (t?.status === 'success') {
              methodStats[method].successCount += 1;
            }
          });

          const methodsArray = Object.values(methodStats)?.map(m => ({
            name: m?.name,
            provider: 'Multi-Provider',
            successRate: m?.total > 0 ? (m?.successCount / m?.total) * 100 : 0,
            volume: m?.volume,
            revenue: m?.revenue,
            trend: 'up',
            trendValue: '+8.2%'
          }));
          setPaymentMethods(methodsArray);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Real-time subscription for new transactions
  useEffect(() => {
    const unsubscribe = transactionsService?.subscribe((payload) => {
      if (payload?.eventType === 'INSERT' && payload?.new) {
        const newTransaction = payload?.new;
        setLiveTransactions((prev) => [newTransaction, ...prev?.slice(0, 19)]);
        
        // Update KPI data
        setKpiData(prev => ({
          ...prev,
          totalVolume: {
            ...prev?.totalVolume,
            value: prev?.totalVolume?.value + 1
          }
        }));
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const handleDrillDown = (data) => {
    console.log('Drill down data:', data);
  };

  const handleFilterChange = (filters) => {
    console.log('Filter changed:', filters);
  };

  const handleRefreshIntervalChange = (interval) => {
    console.log('Refresh interval changed:', interval);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="main-content">
          <div className="content-container">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading transaction data...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="main-content">
        <div className="content-container">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
              Transaction Overview Dashboard
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Monitoring real-time transaksi dan performa payment gateway
            </p>
          </div>

          <div className="mb-6">
            <FilterControls
              onFilterChange={handleFilterChange}
              onRefreshIntervalChange={handleRefreshIntervalChange}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            <KPICard
              title="Total Volume Transaksi"
              value={kpiData?.totalVolume?.value}
              trend={kpiData?.totalVolume?.trend}
              trendValue={kpiData?.totalVolume?.trendValue}
              icon={kpiData?.totalVolume?.icon}
              sparklineData={kpiData?.totalVolume?.sparkline}
              format="number"
              status={kpiData?.totalVolume?.status}
            />
            <KPICard
              title="Success Rate"
              value={kpiData?.successRate?.value}
              trend={kpiData?.successRate?.trend}
              trendValue={kpiData?.successRate?.trendValue}
              icon={kpiData?.successRate?.icon}
              sparklineData={kpiData?.successRate?.sparkline}
              format="percentage"
              status={kpiData?.successRate?.status}
            />
            <KPICard
              title="Total Revenue"
              value={kpiData?.revenue?.value}
              trend={kpiData?.revenue?.trend}
              trendValue={kpiData?.revenue?.trendValue}
              icon={kpiData?.revenue?.icon}
              sparklineData={kpiData?.revenue?.sparkline}
              format="currency"
              status={kpiData?.revenue?.status}
            />
            <KPICard
              title="Active Merchants"
              value={kpiData?.activeMerchants?.value}
              trend={kpiData?.activeMerchants?.trend}
              trendValue={kpiData?.activeMerchants?.trendValue}
              icon={kpiData?.activeMerchants?.icon}
              sparklineData={kpiData?.activeMerchants?.sparkline}
              format="number"
              status={kpiData?.activeMerchants?.status}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
            <div className="lg:col-span-2">
              <TransactionChart data={chartData} onDrillDown={handleDrillDown} />
            </div>
            <div className="lg:col-span-1">
              <LiveTransactionFeed transactions={liveTransactions} maxItems={10} />
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <PaymentMethodPerformance methods={paymentMethods} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default TransactionOverview;